import React, { useState } from 'react';
import { PK } from '../constant/cons';
import { Elements } from '@stripe/react-stripe-js';
import CheckoutForm from './CheckoutForm';
import { loadStripe } from '@stripe/stripe-js';
import { presetProducts } from '../constant/products';
import { siteConfig } from '../constant/siteConfig';
import { currencyConfig } from '../constant/currency';
import { useLanguage } from '../i18n/LanguageContext';
import { initializeSubscription } from '../services/stripeApi';

const stripePromise = loadStripe(PK);

const SubscriptionPayment = () => {
    const { t } = useLanguage();

    // State for email and client secret returned by Stripe (to complete payment)
    const [email, setEmail] = useState('');
    const [clientSecret, setClientSecret] = useState('');
    const [loading, setLoading] = useState(false);

    // Toggle subscription mode: "manual" for custom subscription or "preset" for a pre‐configured product
    const [subscriptionMode, setSubscriptionMode] = useState('preset');

    // Fields for manual subscription
    const [manualAmount, setManualAmount] = useState('10.00'); // default amount input
    const [currency, setCurrency] = useState(currencyConfig.defaultCurrency); // dynamic currency

    // Split billing interval into type and count (removing the redundant "interval" state)
    const [intervalType, setIntervalType] = useState('month');
    const [intervalCount, setIntervalCount] = useState(1);

    // Field for preset subscription (using dummy product data)
    const [presetProduct, setPresetProduct] = useState('');

    const normalizeEmail = (value = '') => value.trim().toLowerCase();

    // Handler for subscription creation
    const handleSubscription = async () => {
        const normalizedEmail = normalizeEmail(email);

        if (!normalizedEmail) {
            alert(t('subscription.alerts.emailRequired'));
            return;
        }
        if (subscriptionMode === 'preset' && !presetProduct) {
            alert(t('subscription.alerts.presetRequired'));
            return;
        }

        setLoading(true);
        try {
            if (normalizedEmail !== email) {
                setEmail(normalizedEmail);
            }
            const payload = {
                email: normalizedEmail,
                mode: subscriptionMode,
            };

            if (subscriptionMode === 'manual') {
                payload.manualAmount = manualAmount;
                payload.currency = currency;
                payload.intervalType = intervalType;
                payload.intervalCount = intervalCount;
            } else {
                payload.presetPriceId = presetProduct;
            }

            const response = await initializeSubscription(payload);
            setClientSecret(response.clientSecret);
        } catch (error) {
            console.error('Subscription error:', error);
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                t('subscription.alerts.createError');
            alert(errorMessage);
        }
        setLoading(false);
    };

    // Options for the Stripe Elements component (if needed)
    const options = {
        clientSecret,
        appearance: {
            theme: 'stripe',
        },
    };

    return (
        <div className="container mx-auto ">
            <div className="card">
                {/* If clientSecret is not yet set, show the subscription form */}
                {!clientSecret ? (
                    <>
                        {/* Email Input */}
                        <div className="form-control mb-4">
                            <label className="label">
                                <span className="label-text">{t('common.email')}</span>
                            </label>
                            <input
                                type="email"
                                className="input input-bordered"
                                value={email}
                                onChange={(e) => setEmail(normalizeEmail(e.target.value))}
                                required
                            />
                        </div>

                        {/* Tab Switch for Subscription Modes - Mobile Responsive */}
                        <div className="tabs transition-all duration-300 ease-in-out overflow-auto tabs-bordered mb-4">
                            
                            <button
                                className={`tab text-nowrap transition-all duration-300 ease-in-out ${subscriptionMode === 'preset' ? 'tab-active font-semibold' : 'opacity-75'}`}
                                onClick={() => setSubscriptionMode('preset')}
                            >
                                {t('subscription.standardSubscription')}
                            </button>

                            <button
                                className={`tab text-nowrap transition-all duration-300 ease-in-out ${subscriptionMode === 'manual' ? 'tab-active font-semibold' : 'opacity-75'}`}
                                onClick={() => setSubscriptionMode('manual')}
                            >
                                {t('subscription.manualSubscription')}
                            </button>
                        </div>

                        {/* Manual Subscription Fields */}
                        {subscriptionMode === 'manual' && (
                            <div className="space-y-4">
                               
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">{t('common.currency')}</span>
                                    </label>
                                    <select
                                        className="select select-bordered"
                                        value={currency}
                                        onChange={(e) => setCurrency(e.target.value)}
                                    >
                                        {currencyConfig.supportedCurrencies.map((item) => (
                                            <option key={item.code} value={item.code}>
                                                {item.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">{t('common.amountWithCurrency', { currency: currency.toUpperCase() })}</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered"
                                        value={manualAmount}
                                        onChange={(e) => setManualAmount(e.target.value)}
                                        placeholder="0.00"
                                        required
                                    />
                                </div>
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">{t('subscription.billingInterval')}</span>
                                    </label>
                                    <select
                                        className="select select-bordered"
                                        value={`${intervalType}_${intervalCount}`}
                                        onChange={(e) => {
                                            const [type, count] = e.target.value.split('_');
                                            setIntervalType(type);
                                            setIntervalCount(parseInt(count, 10));
                                        }}
                                    >
                                        <option value="day_1">{t('subscription.intervals.day1')}</option>
                                        <option value="week_1">{t('subscription.intervals.week1')}</option>
                                        <option value="month_1">{t('subscription.intervals.month1')}</option>
                                        <option value="month_2">{t('subscription.intervals.month2')}</option>
                                        <option value="month_3">{t('subscription.intervals.month3')}</option>
                                        <option value="month_4">{t('subscription.intervals.month4')}</option>
                                        <option value="month_6">{t('subscription.intervals.month6')}</option>
                                        <option value="year_1">{t('subscription.intervals.year1')}</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        {/* Preset Subscription Fields */}
                        {subscriptionMode === 'preset' && (
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">{t('subscription.selectProduct')}</span>
                                </label>
                                <select
                                    className="select select-bordered"
                                    value={presetProduct}
                                    onChange={(e) => setPresetProduct(e.target.value)}
                                >
                                    <option value="">{t('subscription.selectAProduct')}</option>
                                    {presetProducts.map((product) => (
                                        <option key={product.id} value={product.id}>
                                            {product.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <button
                            className="btn w-full mt-4 hover:opacity-90"
                            style={{
                                backgroundColor: siteConfig.brandColors.primary,
                                color: siteConfig.brandColors.buttonText,
                            }}
                            onClick={handleSubscription}
                            disabled={loading}
                        >
                            {loading ? <span className="loading loading-spinner"></span> : t('subscription.createSubscription')}
                        </button>
                    </>
                ) : (
                    // Once a clientSecret is available, render Stripe Elements with the CheckoutForm
                    <Elements stripe={stripePromise} options={options}>
                        <CheckoutForm paymentType={'subscription'} email={normalizeEmail(email)} />
                    </Elements>
                )}
            </div>
        </div>
    );
};

export default SubscriptionPayment;
